import api from '../apiClient';

// Master Data domain API modules — currencies, brands, markets, axes,
// suppliers, segments, exchange rates, brand groups, contractors. Split out of
// the monolithic services/api.js; re-exported from there so existing imports
// keep working. Bodies are verbatim from the original file.

// Currency API
export const currencyAPI = {
  getAll: () => api.get('/currencies'),
  getActive: () => api.get('/currencies/active'),
  getExchangeRates: () => api.get('/currencies/exchange-rates'),
  getById: (id) => api.get(`/currencies/${id}`),
  create: (data) => api.post('/currencies', data),
  update: (id, data) => api.put(`/currencies/${id}`, data),
  updateExchangeRate: (id, data) => api.patch(`/currencies/${id}/exchange-rate`, data),
  delete: (id) => api.delete(`/currencies/${id}`),
  refreshRates: () => api.put('/currencies/refresh-rates'),
  getRateHistory: (id, params) => api.get(`/currencies/${id}/rate-history`, { params }),
};

// Brand API
export const brandAPI = {
  getAll: () => api.get('/brands'),
  getActive: () => api.get('/brands/active'),
  getById: (id) => api.get(`/brands/${id}`),
  getBySupplier: (supplierId) => api.get(`/brands/supplier/${supplierId}`),
  create: (data) => api.post('/brands', data),
  update: (id, data) => api.put(`/brands/${id}`, data),
  delete: (id) => api.delete(`/brands/${id}`),
  bulkDelete: (brandIds) => api.post('/brands/bulk-delete', { brandIds }),
  // segment: a Segment id, or null for no segment.
  bulkSetSegment: (brandIds, segment) => api.post('/brands/bulk-segment', { brandIds, segment }),
  // Admin: exclude (true) / include brands from the Brand List MRP Dashboard,
  // Update Prices to Shopify and Shopify Stock.
  setShopifyExclusion: (brandIds, excluded) => api.patch('/brands/shopify-exclusion', { brandIds, excluded })
};

// Market API
export const marketAPI = {
  getAll: () => api.get('/markets'),
  getById: (id) => api.get(`/markets/${id}`),
  create: (data) => api.post('/markets', data),
  update: (id, data) => api.put(`/markets/${id}`, data),
  delete: (id) => api.delete(`/markets/${id}`),
  bulkDelete: (marketIds) => api.post('/markets/bulk-delete', { marketIds })
};

// Axis API — drives the master list of product-category axes shown in
// pickers across Business Plans and Brands.
export const axisAPI = {
  getAll: () => api.get('/axes'),
  create: (data) => api.post('/axes', data),
  update: (id, data) => api.put(`/axes/${id}`, data),
  delete: (id) => api.delete(`/axes/${id}`),
  reorder: (order) => api.put('/axes/reorder', { order })
};

// Supplier API
export const supplierAPI = {
  getAll: () => api.get('/suppliers'),
  getActive: () => api.get('/suppliers/active'),
  getById: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
  bulkDelete: (supplierIds) => api.post('/suppliers/bulk-delete', { supplierIds }),
  updateExchangeRate: (id, data) => api.patch(`/suppliers/${id}/exchange-rate`, data),
  getExchangeRateHistory: (id) => api.get(`/suppliers/${id}/exchange-rate-history`),
  validateInventree: () => api.get('/suppliers/validate-inventree')
};

// Brand Group (Reporting Group) API
export const brandGroupAPI = {
  getAll:     ()        => api.get('/brand-groups'),
  getById:    (id)      => api.get(`/brand-groups/${id}`),
  create:     (data)    => api.post('/brand-groups', data),
  update:     (id, d)   => api.put(`/brand-groups/${id}`, d),
  delete:     (id)      => api.delete(`/brand-groups/${id}`),
  // Members + per-brand values in one call. Optional maps keyed by brand id:
  // `recipients` { emailTo: [...], emailCc: [...] }, `reportCreation` bool,
  // `monthlyReportRequired` bool. With id '__ind__' membership is unchanged and
  // the maps apply only to brands with no group.
  setMembers: (id, ids, recipients, reportCreation, monthlyReportRequired) => api.put(
    `/brand-groups/${id}/members`,
    {
      brandIds: ids,
      ...(recipients ? { recipients } : {}),
      ...(reportCreation ? { reportCreation } : {}),
      ...(monthlyReportRequired ? { monthlyReportRequired } : {}),
    }
  ),
  // Synthetic "Independent (IND)" pseudo-group — brands with no Brand.group ref.
  // Returns { _id: '__ind__', groupName, synthetic: true, brands: [...] }.
  getIndependent: () => api.get('/brand-groups/independent'),

  // Download the full Reporting-Groups roster as .xlsx (one row per
  // brand-to-group assignment, plus an "IND" row for independent brands).
  // Returns a Blob via axios so the caller can trigger a browser download.
  export: () => api.get('/brand-groups/export', { responseType: 'blob' }),
};

// Exchange Rate API
export const exchangeRateAPI = {
  getAll: () => api.get('/exchange-rates'),
  getLatest: () => api.get('/exchange-rates/latest'),
  getByCurrency: (currencyId) => api.get(`/exchange-rates/currency/${currencyId}`),
  create: (data) => api.post('/exchange-rates', data),
  update: (id, data) => api.put(`/exchange-rates/${id}`, data),
  delete: (id) => api.delete(`/exchange-rates/${id}`),
};

// Segment API
export const segmentAPI = {
  getAll: () => api.get('/segments'),
  getById: (id) => api.get(`/segments/${id}`),
  create: (data) => api.post('/segments', data),
  update: (id, data) => api.put(`/segments/${id}`, data),
  delete: (id) => api.delete(`/segments/${id}`),
};

// Contractor API (marketing vendors / contractors master list)
export const contractorAPI = {
  getAll: (params) => api.get('/contractors', { params }),
  getActive: () => api.get('/contractors/active'),
  search: (q) => api.get('/contractors/search', { params: { q } }),
  getById: (id) => api.get(`/contractors/${id}`),
  create: (data) => api.post('/contractors', data),
  update: (id, data) => api.put(`/contractors/${id}`, data),
  delete: (id) => api.delete(`/contractors/${id}`),
  bulkDelete: (contractorIds) => api.post('/contractors/bulk-delete', { contractorIds }),
  uploadLogo: (id, formData) => api.post(`/contractors/${id}/logo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Returns { url } — a short-lived viewing URL for an <img src>, or null while
  // the logo is still on the server's local disk.
  getLogoUrl: (id) => api.get(`/contractors/${id}/logo`),
  deleteLogo: (id) => api.delete(`/contractors/${id}/logo`)
};

// Seasonality Calendar (admin only): the Bangladesh retail calendar explained per year,
// and per-year overrides of its 12 monthly weights.
export const seasonalCalendarAPI = {
  // { range: { from, to }, overrides: [{ year, updatedAt, updatedByName }] }
  getOverview: () => api.get('/seasonal-calendar'),
  // { year, source, months, events, override, history }
  getYear: (year) => api.get(`/seasonal-calendar/${year}`),
  // weights: { jan..dec: Number }, each rounded to 2 dp, totalling exactly 100; reason: 3–500 characters.
  setOverride: (year, weights, reason) => api.put(`/seasonal-calendar/${year}`, { weights, reason }),
  // The override goes; its history stays.
  removeOverride: (year, reason) => api.delete(`/seasonal-calendar/${year}`, { data: { reason } }),
};
